import Component from '@glimmer/component';
import FilterControl from './filter-control.gts';
import type { FilterFieldControl } from '../filter-bar';
import type { Filter } from '../../../utils/filters';
import { HdsButton } from '@hashicorp/design-system-components/components';
import { on } from '@ember/modifier';

export interface FilterControlGroupSignature {
  Args: {
    controls: FilterFieldControl[];
    appliedFilters: Record<string, Filter>;
    onUpdateFilters: (filters: Record<string, Filter>) => void;
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class FilterControlGroup extends Component<FilterControlGroupSignature> {
  handleSubmit = (event: Event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);

    const filters = this.args.controls.reduce(
      (acc: Record<string, Filter>, control) => {
        const key = control.name;

        const allValuesForKey = formData.getAll(key) as string[];

        if (allValuesForKey.length === 0) {
          delete acc[key];
          return acc;
        }

        // default operator and simple string value
        let operator: Filter['operator'] = '=';
        let value: Filter['value']['value'] = allValuesForKey?.[0] || '';
        let type: Filter['value']['type'] = 'string';

        // lookback/date filters have different type and operators
        if (control?.type === 'lookback') {
          const days = parseInt(value, 10);
          operator = days < 0 ? '>' : '<';
          type = 'duration';
        }
        // multiselect are array values
        if (control?.type === 'multi-select') {
          value = allValuesForKey;
          type = 'list';
        }

        const filter: Filter = {
          field: key,
          operator,
          value: {
            type,
            value,
          },
        };

        return {
          ...acc,
          [key]: filter,
        };
      },
      this.args.appliedFilters,
    );

    this.args.onUpdateFilters(filters);
  };

  <template>
    <form
      class="filter-bar__control-group"
      data-test-vault-reporting-filter-bar-control-group
      {{on "submit" this.handleSubmit}}
    >
      {{#each @controls as |control|}}
        <FilterControl
          @filter={{control}}
          @appliedFilters={{@appliedFilters}}
        />
      {{/each}}
      <HdsButton
        class="filter-bar__control-group-submit"
        type="submit"
        @text="Apply"
      />
    </form>
  </template>
}
